import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * TripCardSkeleton - Loading placeholder matching TripCard layout
 *
 * Mirrors the responsive grid structure of TripCard:
 * - Mobile: Stacked vertical layout
 * - Tablet: 2-column departure/arrival, full-width journey + price
 * - Desktop: 5-column horizontal layout with logo
 */
export function TripCardSkeleton() {
  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[auto_1fr_auto_1fr_auto] gap-4 lg:gap-6 items-start">
            {/* Column 1: Logo placeholder (desktop only) */}
            <div className="hidden lg:flex items-center justify-center w-16">
              <Skeleton className="w-12 h-12 rounded-full" />
            </div>

            {/* Column 2: Departure skeleton */}
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-16" /> {/* "Departure" label */}
              <Skeleton className="h-4 w-32" /> {/* Date */}
              <Skeleton className="h-7 w-24" /> {/* Time */}
              <Skeleton className="h-4 w-28" /> {/* Location */}
            </div>

            {/* Column 3: Journey center skeleton */}
            <div className="flex flex-col items-center justify-center px-4 py-2 sm:col-span-2 lg:col-span-1 border-y sm:border-y-0 sm:border-x border-border/50 lg:border-0">
              <Skeleton className="w-5 h-5 rounded mb-2" /> {/* Bus icon */}
              <Skeleton className="h-4 w-16" /> {/* Duration */}
              <Skeleton className="h-8 w-20 mt-2" /> {/* Show stops button */}
            </div>

            {/* Column 4: Arrival skeleton */}
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-14" /> {/* "Arrival" label */}
              <Skeleton className="h-4 w-32" /> {/* Date */}
              <Skeleton className="h-7 w-24" /> {/* Time */}
              <Skeleton className="h-4 w-28" /> {/* Location */}
            </div>

            {/* Column 5: Price and action skeleton */}
            <div className="flex flex-col sm:flex-row lg:flex-col items-center sm:items-end justify-between gap-3 sm:col-span-2 lg:col-span-1 pt-3 border-t lg:border-0 lg:pt-0">
              <div className="flex flex-col items-center sm:items-end gap-1">
                <Skeleton className="h-8 w-20" /> {/* Price */}
                <Skeleton className="h-3 w-16" /> {/* Price details */}
              </div>
              <div className="flex flex-col items-center sm:items-end gap-2 w-full sm:w-auto">
                <Skeleton className="h-4 w-14" /> {/* Seats available */}
                <Skeleton className="h-11 w-full sm:w-28" /> {/* Select button */}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * TripCardSkeletonList - Renders multiple skeleton cards for loading state
 */
export function TripCardSkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <TripCardSkeleton key={index} />
      ))}
    </div>
  );
}
